import type { EvidenceSourceType, InsightEvidenceGroup, InsightEvidenceItem } from "~vue/api/generated/intelligence-evidence";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS } from "~vue/components/navigation/menuPanes";
import { i18n } from "~vue/i18n";
import type { LabInsightLocation } from "~vue/types/labs";

export type { EvidenceSourceType, InsightEvidenceGroup, InsightEvidenceItem };

export type InsightEvidenceAnalyticsContext = {
    insightId: string;
    experimentSlug: string;
    location: LabInsightLocation;
};

const EVIDENCE_SOURCE_WEIGHTS: Record<EvidenceSourceType, number> = {
    collaboration_profile: 1,
    past_chat: 2,
    user_memory: 2,
    user_profile_note: 2,
    connection_note: 3,
    lab_insight: 4,
    perspective: 5,
    pair_perspective: 5,
    calendar_event: 6,
    granola_meeting: 7,
    workiq_outlook: 7,
    workiq_teams: 7,
    workiq_calendar: 7,
    workiq_documents: 7,
    workiq_microsoft_365: 7,
    other: 7,
    performance_review_conversation: 8,
    performance_review_upload: 8,
};

export type EvidenceSourceCounts = Partial<Record<EvidenceSourceType, number>>;

export function sortEvidenceGroups(groups: InsightEvidenceGroup[]): InsightEvidenceGroup[] {
    return [...groups].sort((left, right) => EVIDENCE_SOURCE_WEIGHTS[left.kind] - EVIDENCE_SOURCE_WEIGHTS[right.kind]);
}

export function evidenceSourceCounts(groups: InsightEvidenceGroup[]): EvidenceSourceCounts {
    return groups.reduce<EvidenceSourceCounts>((counts, group) => {
        counts[group.kind] = (counts[group.kind] ?? 0) + group.count;
        return counts;
    }, {});
}

/** Perspective sources use the PerspectiveCircles mark in EvidenceSourceIcon. */
export const EVIDENCE_ICON_CLASSES: Record<Exclude<EvidenceSourceType, "perspective" | "pair_perspective">, string> = {
    calendar_event: "bi-calendar3",
    granola_meeting: "bi-journal-text",
    workiq_outlook: "bi-envelope",
    workiq_teams: "bi-people-fill",
    workiq_calendar: "bi-calendar-event",
    workiq_documents: "bi-file-earmark-text",
    workiq_microsoft_365: "bi-microsoft",
    past_chat: "bi-chat-left-text",
    user_memory: "bi-journal-bookmark",
    user_profile_note: "bi-journal-text",
    connection_note: "bi-person-lines-fill",
    collaboration_profile: COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS,
    lab_insight: "bi-lightbulb",
    performance_review_conversation: "bi-clipboard-data",
    performance_review_upload: "bi-clipboard-data",
    other: "bi-link-45deg",
};

export function evidenceChipLabel(group: InsightEvidenceGroup): string {
    if (group.kind === "user_profile_note") {
        return i18n.global.t("labs.evidence.profileNotes", { count: group.count });
    }
    return group.chip_label ?? group.label;
}

/** Singular/plural noun for the redesigned "Based on: 2 calendar events" inline-text treatment. */
const EVIDENCE_LINK_NOUNS: Record<EvidenceSourceType, { singular: string; plural: string }> = {
    calendar_event: { singular: "calendar event", plural: "calendar events" },
    granola_meeting: { singular: "Granola meeting", plural: "Granola meetings" },
    workiq_outlook: { singular: "Outlook email", plural: "Outlook emails" },
    workiq_teams: { singular: "Teams message", plural: "Teams messages" },
    workiq_calendar: { singular: "Outlook Calendar event", plural: "Outlook Calendar events" },
    workiq_documents: { singular: "document", plural: "documents" },
    workiq_microsoft_365: { singular: "Microsoft 365 item", plural: "Microsoft 365 items" },
    past_chat: { singular: "past conversation", plural: "past conversations" },
    perspective: { singular: "Perspective", plural: "Perspective" },
    pair_perspective: { singular: "Pair Perspective", plural: "Pair Perspectives" },
    connection_note: { singular: "people note", plural: "people notes" },
    collaboration_profile: { singular: "collaboration profile", plural: "collaboration profiles" },
    performance_review_conversation: { singular: "performance review", plural: "performance reviews" },
    performance_review_upload: { singular: "performance review", plural: "performance reviews" },
    lab_insight: { singular: "past insight", plural: "past insights" },
    user_memory: { singular: "memory", plural: "memories" },
    user_profile_note: { singular: "profile note", plural: "profile notes" },
    other: { singular: "other source", plural: "other sources" },
};

/**
 * The chip's bare-digit badge ("Calendar 2") reads fine next to an icon, but sits oddly inline
 * in a sentence ("...and Calendar2"). `chip_label` already spells out the self/named-person case
 * (always count === 1, see `_collaboration_profile_chip_label`), so only the generic fallback
 * needs a "N noun(s)" phrase built here.
 */
export function evidenceLinkText(group: InsightEvidenceGroup): string {
    if (group.kind === "user_profile_note") {
        return evidenceChipLabel(group);
    }
    if (group.chip_label) {
        return group.chip_label;
    }
    const noun = EVIDENCE_LINK_NOUNS[group.kind];
    return `${group.count} ${group.count === 1 ? noun.singular : noun.plural}`;
}

export function calculateVisibleEvidenceGroupCount(containerWidth: number, labelWidth: number, groupWidths: number[], overflowWidths: number[], gap = 8): number {
    const labelGapCount = labelWidth > 0 ? 1 : 0;
    const allGroupsWidth = labelWidth + groupWidths.reduce((total, width) => total + width, 0) + gap * Math.max(groupWidths.length + labelGapCount - 1, 0);
    if (allGroupsWidth <= containerWidth) {
        return groupWidths.length;
    }

    let visibleWidth = labelWidth;
    let visibleGroups = 0;
    for (let visibleCount = 0; visibleCount < groupWidths.length; visibleCount += 1) {
        const hiddenCount = groupWidths.length - visibleCount;
        const overflowWidth = overflowWidths[hiddenCount - 1] ?? 0;
        const requiredWidth = visibleWidth + overflowWidth + gap * (visibleCount + labelGapCount);
        if (requiredWidth > containerWidth) {
            break;
        }
        visibleGroups = visibleCount;
        visibleWidth += groupWidths[visibleCount] ?? 0;
    }

    return visibleGroups;
}
